import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTicket,
  faChalkboardTeacher,
  faLandmark,
  faPalette,
  faTree,
  faPaw,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';
import config from '../../../config/environment';
import { focusInvalidField, getFieldErrors } from '../../../utils/formFieldErrors';

const bannerImage = 'https://BelvoirCastle.b-cdn.net/Banners/1.webp';

const GROUPS_PHONE = '01476 871001';
const GROUPS_EMAIL = 'groups@belvoircastle.com';

const ARRIVAL_TIMES = ['10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30'];

const EMPTY_FORM = {
  name: '',
  school: '',
  email: '',
  phone: '',
  pupils: '',
  teachers: '',
  preferredDate: '',
  preferredTime: '',
  specialRequests: ''
};

const BENEFITS = [
  {
    icon: faTicket,
    title: 'Special School Entry Price',
    text: '£12 per pupil, subject to a minimum of 15 pupils per visit.'
  },
  {
    icon: faChalkboardTeacher,
    title: 'Free Entry for Teachers and Support Staff',
    text: 'Accompanying teachers and support staff go free, in line with our required supervisor ratios.'
  },
  {
    icon: faLandmark,
    title: 'Age-appropriate Guided Tours',
    text: 'Guided tours of the Castle tailored to the age of your pupils.'
  },
  {
    icon: faPalette,
    title: 'Creative Activities',
    text: 'Creative activities that bring the stories of Belvoir Castle to life.'
  },
  {
    icon: faTree,
    title: 'Adventure Playground and Gardens',
    text: 'Time to explore the Adventure Playground and the Castle\'s beautiful gardens.'
  },
  {
    icon: faPaw,
    title: 'Option to include the Animal Park',
    text: 'Add the Animal Park to your school visit.'
  }
];

const SchoolsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 100%;
  margin-bottom: 1.5rem;
  text-align: left;

  &:last-of-type {
    margin-bottom: 3rem;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const BenefitCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h4 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin: 1rem 0;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;

  svg {
    color: white;
    font-size: 28px;
  }
`;

const FormSection = styled.div`
  margin-top: 4rem;
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 2.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: ${props => (props.$full ? '1 / -1' : 'auto')};
`;

const GroupSizeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
  gap: 1rem;
`;

const Label = styled.label`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.primary};
`;

const SubLabel = styled.label`
  font-size: 0.95rem;
  margin-bottom: 0.35rem;
  color: ${props => props.theme.colors.secondary};
`;

const invalidStyles = `
  &[aria-invalid='true'] {
    border-color: #b3261e;
    background-color: #fdf3f3;
  }
`;

const FieldError = styled.span`
  font-size: 0.9rem;
  margin-top: 0.4rem;
  color: #b3261e;
`;

const Hint = styled.span`
  font-size: 0.9rem;
  margin-top: 0.4rem;
  color: ${props => props.theme.colors.secondary};
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }

  ${invalidStyles}
`;

const Select = styled.select`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }

  ${invalidStyles}
`;

const TextArea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 150px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }

  ${invalidStyles}
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  border: 2px solid transparent;
  border-radius: 25px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  grid-column: 1 / -1;
  justify-self: start;
  margin-top: 1rem;

  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

const ThankYou = styled.div`
  text-align: center;
  padding: 1.5rem 0.5rem;

  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    margin: 1.25rem 0 1rem;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: ${props => props.theme.colors.secondary};
    max-width: 560px;
    margin: 0 auto 1rem;
  }
`;

const ThankYouIcon = styled(IconWrapper)`
  margin: 0 auto;
`;

const SecondaryButton = styled(SubmitButton)`
  background-color: transparent;
  color: ${props => props.theme.colors.accent};
  border-color: ${props => props.theme.colors.accent};
  margin-top: 0.5rem;

  &:hover {
    background-color: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const Schools = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = event => {
    const { name, value } = event.target;
    if (fieldErrors[name]) {
      setFieldErrors(({ [name]: _cleared, ...rest }) => rest);
      if (Object.keys(fieldErrors).length === 1) setSubmitStatus(null);
    }
    setFormData(current => ({ ...current, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const { errors, firstInvalid } = getFieldErrors(event.currentTarget);
    setFieldErrors(errors);
    if (firstInvalid) {
      setSubmitStatus({ type: 'error', message: 'Please complete the highlighted fields.' });
      focusInvalidField(firstInvalid);
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${config.apiEndpoint}/belvoir/v1/school-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          pupils: Number(formData.pupils),
          teachers: Number(formData.teachers)
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit enquiry');
      }

      setSubmitStatus({ type: 'success', school: formData.school.trim() });
      setFormData(EMPTY_FORM);

      const formTop = document.getElementById('schoolForm');
      if (formTop) {
        window.scrollTo({ top: formTop.offsetTop - 100, behavior: 'smooth' });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SchoolsContainer>
      <Helmet>
        <title>School Visits | Belvoir Castle</title>
        <meta
          name="description"
          content="School visits to Belvoir Castle, a living history classroom. £12 per pupil (minimum 15), free entry for teachers and support staff, guided tours, creative activities, gardens and the Adventure Playground."
        />
        <link rel="canonical" href={`${config.frontendUrl}/your-visit/schools`} />
      </Helmet>

      <ParallaxBanner
        title="School Visits"
        subtitle="A Living History Classroom"
        image={bannerImage}
      />

      <ContentContainer>
        <SectionTitle>Belvoir Castle - A Living History Classroom</SectionTitle>
        <IntroText>
          We are delighted to welcome schools to Belvoir Castle, where history, heritage and fascinating
          stories come together to create an unforgettable learning experience.
        </IntroText>
        <IntroText>
          Bring learning to life with a visit to Belvoir Castle, one of England’s most remarkable historic
          estates. Students can explore magnificent State Rooms, discover over a thousand years of history,
          and uncover captivating stories of royalty, art and architecture. They can also enjoy the castle’s
          beautiful gardens and landscapes, shaped by centuries of horticultural excellence and inspired design.
        </IntroText>
        <IntroText>
          From grand interiors to stunning outdoor spaces, Belvoir Castle offers an engaging, educational and
          memorable day out that inspires curiosity and enriches classroom learning.
        </IntroText>

        <BenefitsGrid>
          {BENEFITS.map(benefit => (
            <BenefitCard key={benefit.title}>
              <IconWrapper>
                <FontAwesomeIcon icon={benefit.icon} />
              </IconWrapper>
              <h4>{benefit.title}</h4>
              <p>{benefit.text}</p>
            </BenefitCard>
          ))}
        </BenefitsGrid>

        <FormSection id="schoolForm">
          <SectionTitle>Make a School Visit Enquiry</SectionTitle>
          <IntroText>
            Please complete the form below to enquire about your school visit. Our dedicated team will
            respond to your enquiry within 48 hours to discuss your requirements and help plan your school visit.
          </IntroText>

          <FormCard>
            {submitStatus?.type === 'success' ? (
              <ThankYou role="status">
                <ThankYouIcon>
                  <FontAwesomeIcon icon={faCheck} />
                </ThankYouIcon>
                <h3>Thank you for your enquiry</h3>
                <p>
                  We have received your school visit enquiry{submitStatus.school ? ` for ${submitStatus.school}` : ''}.
                  Our Groups Team will be in touch within 48 hours to discuss your requirements and help plan your visit.
                </p>
                <p>
                  If you need us sooner, call {GROUPS_PHONE} or email{' '}
                  <a href={`mailto:${GROUPS_EMAIL}`} style={{ color: theme.colors.accent }}>{GROUPS_EMAIL}</a>.
                </p>
                <SecondaryButton type="button" onClick={() => setSubmitStatus(null)}>
                  Send another enquiry
                </SecondaryButton>
              </ThankYou>
            ) : (
              <>
              {submitStatus?.type === 'error' && <ErrorMessage>{submitStatus.message}</ErrorMessage>}

              <Form onSubmit={handleSubmit} noValidate>
                <FormGroup>
                  <Label htmlFor="name">Contact Name *</Label>
                  <Input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required aria-invalid={fieldErrors.name ? 'true' : undefined} />
                  {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="school">School Name *</Label>
                  <Input type="text" id="school" name="school" value={formData.school} onChange={handleChange} required aria-invalid={fieldErrors.school ? 'true' : undefined} />
                  {fieldErrors.school && <FieldError>{fieldErrors.school}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email *</Label>
                  <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required aria-invalid={fieldErrors.email ? 'true' : undefined} />
                  {fieldErrors.email && <FieldError>{fieldErrors.email}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required aria-invalid={fieldErrors.phone ? 'true' : undefined} />
                  {fieldErrors.phone && <FieldError>{fieldErrors.phone}</FieldError>}
                </FormGroup>

                <FormGroup $full>
                  <Label as="span">Group Size *</Label>
                  <GroupSizeRow>
                    <FormGroup>
                      <SubLabel htmlFor="pupils">Pupils</SubLabel>
                      <Input type="number" id="pupils" name="pupils" min="15" step="1" inputMode="numeric" value={formData.pupils} onChange={handleChange} required data-required-message="Please enter the number of pupils" data-range-message="Minimum of 15 pupils" aria-invalid={fieldErrors.pupils ? 'true' : undefined} />
                    </FormGroup>
                    <FormGroup>
                      <SubLabel htmlFor="teachers">Teachers / Supervisors</SubLabel>
                      <Input type="number" id="teachers" name="teachers" min="1" step="1" inputMode="numeric" value={formData.teachers} onChange={handleChange} required data-required-message="Please enter the number of teachers / supervisors" aria-invalid={fieldErrors.teachers ? 'true' : undefined} />
                    </FormGroup>
                  </GroupSizeRow>
                  {fieldErrors.pupils && <FieldError>{fieldErrors.pupils}</FieldError>}
                  {fieldErrors.teachers && <FieldError>{fieldErrors.teachers}</FieldError>}
                  <Hint>The special school entry price applies to a minimum of 15 pupils per visit.</Hint>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="preferredDate">Preferred Visit Date *</Label>
                  <Input type="date" id="preferredDate" name="preferredDate" value={formData.preferredDate} onChange={handleChange} required aria-invalid={fieldErrors.preferredDate ? 'true' : undefined} />
                  {fieldErrors.preferredDate && <FieldError>{fieldErrors.preferredDate}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="preferredTime">Preferred Arrival Time *</Label>
                  <Select id="preferredTime" name="preferredTime" value={formData.preferredTime} onChange={handleChange} required aria-invalid={fieldErrors.preferredTime ? 'true' : undefined}>
                    <option value="">Please select</option>
                    {ARRIVAL_TIMES.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </Select>
                  {fieldErrors.preferredTime && <FieldError>{fieldErrors.preferredTime}</FieldError>}
                </FormGroup>

                <FormGroup $full>
                  <Label htmlFor="specialRequests">Special Requests or Questions</Label>
                  <TextArea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleChange}
                    placeholder="Let us know about year groups, accessibility needs, activities you are interested in, or any questions you have."
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                </SubmitButton>
              </Form>
              </>
            )}
          </FormCard>
        </FormSection>

        <FormSection>
          <SectionTitle>Contact Our Groups Team Directly</SectionTitle>
          <IntroText>
            If you prefer to discuss your school visit by phone or email, please contact our dedicated
            Groups Team using the details below:
          </IntroText>

          <FormCard>
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
              <strong>Phone:</strong> <a href={`tel:${GROUPS_PHONE.replace(/\s/g, '')}`} style={{ color: theme.colors.accent }}>{GROUPS_PHONE}</a>
            </p>
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
              <strong>Email:</strong> <a href={`mailto:${GROUPS_EMAIL}`} style={{ color: theme.colors.accent }}>{GROUPS_EMAIL}</a>
            </p>
            <p style={{ fontSize: '1.1rem' }}>
              Our team is available Monday to Friday, 9:00am to 5:00pm to assist with your enquiry.
            </p>
          </FormCard>
        </FormSection>
      </ContentContainer>

      <Footer />
    </SchoolsContainer>
  );
};

export default Schools;
